import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getImageUrl } from '../../../utils';
import useApi from '../../../hooks/useApi';
import ContentEditor from '../../ContentEditor';
import NodeActivityEditor from '../../NodeActivityEditor';
import HeaderToolbar from './HeaderToolbar';
import SecondaryButton from '../../SecondaryButton';
import PrimaryButton from '../../PrimaryButton';
import CommentsIcon from '../../../../public/assets/images/theme-2/icons/asterisk-white.svg';
import {
  ViewSection,
  HeaderUserImage,
  HeaderUserName,
  HeaderDate,
  HeaderRequestFeedback,
  AutoSaveStatus,
  ButtonPanel,
  LockInfo,
} from '../styles';
import {
  EditorHeaderRowLeft,
  EditorHeaderRowRight,
  BlogWrapper,
  BlogPostOptions,
  BlogPostOptionEdit,
} from '../../NodeActivityEditor/styles';
import { SubmitPanel } from '../../NodeContentPreview/styles';
import ImmersiveReaderButton from '../../ImmersiveReaderButton';

const PrivateBlogContentViewer = ({
  report,
  isEditable,
  saveReport,
  fileService,
  integrations,
  autoSaveStatus,
  isTeacher,
  user,
  flash,
  sessionId,
  nodeId,
}) => {
  const isLocked = report?.locked;

  const emptyPost = {
    content: '',
    draft: true,
    type: 'blog_post',
    updated_at: new Date(),
    created_at: new Date().toISOString(),
  };
  const [showToolbar, setShowToolbar] = useState({});
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(emptyPost);
  const [currentPostRef, setCurrentPostRef] = useState();
  const [lastEditedIndex, setLastEditedIndex] = useState();

  const dateSorter = (a, b) => b.created_at.localeCompare(a.created_at);

  useEffect(() => {
    const toolbarToggles = report?.BlogPosts?.reduce((acc, curr) => ((acc[curr.uuid] = false), acc), {}) || {};
    toolbarToggles.current = false;
    const blogPosts = report?.BlogPosts.sort(dateSorter);
    setPosts(blogPosts);
    setShowToolbar(toolbarToggles || {});
  }, []);

  useEffect(() => {
    const blogPosts = (report?.BlogPosts || []).sort(dateSorter);
    if (blogPosts && blogPosts.length > posts?.length && !currentPost?.uuid && report.hasOwnProperty('save')) {
      const newPost = blogPosts[0];
      const { uuid, draft } = newPost;
      if (draft) {
        setCurrentPost({ ...currentPost, uuid });
        const newToolbarToggles = { ...showToolbar, [uuid]: showToolbar.current };
        delete newToolbarToggles['current'];
        setShowToolbar(newToolbarToggles);
      }
    }
  }, [report, posts]);

  useEffect(() => {
    if (currentPost?.draft && currentPost?.uuid && currentPostRef && lastEditedIndex < 0) {
      currentPostRef.focus();
      currentPostRef?.editor?.setSelection(currentPostRef?.editor?.getLength());
    }
  }, [currentPost, currentPostRef, lastEditedIndex]);

  const { full_name, image, auth_token } = user;
  const { deleteData } = useApi(auth_token);
  const getEditorId = (index) => `editor_${index === -1 ? 'current' : index}`;

  const submitReport = () => {
    if (currentPost.content !== '') {
      publishReport(currentPost, -1, true);
    } else {
      saveReport({ ...report, state: 'done', draft: false });
    }
  };

  const revokeReport = () => {
    saveReport({ ...report, state: 'active', draft: true });
  };

  const onChangeReport = (value, blog, index) => {
    const { uuid: post_uuid } = blog;
    const newPosts = [...posts];
    if (index < 0) {
      setCurrentPost({ ...currentPost, content: value, draft: true });
    } else {
      newPosts[index] = { ...blog, content: value, draft: true };
      setPosts(newPosts);
    }
    saveReport({
      ...report,
      editorId: getEditorId(index),
      body: value,
      state: 'active',
      draft: true,
      post_uuid,
      BlogPosts: newPosts,
    });
    setLastEditedIndex(index);
  };

  const publishReport = (blog, index, submit = false) => {
    const { uuid: post_uuid, content } = blog;
    const newPosts = [...posts];
    if (index < 0 && !!content) {
      newPosts.unshift({ ...currentPost, draft: false });
      setCurrentPost({ ...emptyPost });
      setShowToolbar({ ...showToolbar, current: showToolbar[post_uuid] });
    } else {
      newPosts[index] = { ...blog, draft: false };
    }
    setPosts(newPosts);
    saveReport({
      ...report,
      editorId: getEditorId(index),
      body: content,
      state: submit ? 'done' : 'active',
      draft: false,
      post_uuid,
      BlogPosts: newPosts,
    });
  };

  const editPost = (blog, index) => {
    const newPosts = [...posts];
    newPosts[index] = { ...blog, draft: true };
    setPosts(newPosts);
  };

  const removePost = async (post_uuid) => {
    const response = await deleteData(`/api/v3/viewer/behaviors/${nodeId}/session/${sessionId}/post/${post_uuid}`);
    if (response.success) {
      const newPosts = posts.filter((post) => post.uuid !== post_uuid);
      setPosts(newPosts);
      const newToolbarToggles = { ...showToolbar };
      delete newToolbarToggles[post_uuid];
      setShowToolbar(newToolbarToggles);
    } else {
      console.error(response);
      if (response.message === 'auth_failed') {
        flash(i18n.__('app_error_unauthorized'), { ttl: 5000 });
      } else {
        flash(i18n.__('error_behavior_save_failed'));
      }
    }
  };

  const toggleToolbar = (id = 'current') => {
    setShowToolbar({ ...showToolbar, [id]: !showToolbar[id] });
  };

  const toggleReportComments = (post_uuid, type) => {
    document.dispatchEvent(new CustomEvent('toggleComments', { detail: { entityId: post_uuid, entityType: type } }));
  };

  const currentEditorPanel = () => {
    const { uuid: post_uuid, content, type = 'blog_post' } = currentPost || {};
    const id = getEditorId(-1);
    const onRef = (ref) => {
      setCurrentPostRef(ref.current);
    };

    return (
      <ContentEditor
        id={id}
        type={type}
        onRef={onRef}
        aria-label={i18n.__('logbook_texteditor_placeholder')}
        placeholder={i18n.__('logbook_texteditor_placeholder')}
        value={content || ''}
        onChange={(value) => isEditable && onChangeReport(value, currentPost, -1)}
        integrations={integrations}
        fileService={fileService}
        disabled={!isEditable || isTeacher}
        showToolbar={showToolbar[post_uuid || 'current']}
        className={'ir-content'}
        memoize={false}
      />
    );
  };

  const editorPanels = (posts || []).map((blog, index) => {
    const { uuid: post_uuid, content, type = 'blog_post', draft } = blog || {};
    const id = getEditorId(index);

    return (
      <ContentEditor
        key={id}
        id={id}
        type={type}
        aria-label={i18n.__('logbook_texteditor_placeholder')}
        placeholder={i18n.__('logbook_texteditor_placeholder')}
        value={content || ''}
        onChange={(value) => isEditable && onChangeReport(value, blog, index)}
        integrations={integrations}
        fileService={fileService}
        disabled={!isEditable || isTeacher || !draft}
        showToolbar={showToolbar[post_uuid]}
        className={'ir-content'}
        memoize={true}
      />
    );
  });

  const addPost = (blog, index) => {
    const { uuid: post_uuid, content, draft, type = 'blog_post', updated_at } = blog || {};
    const editorId = getEditorId(index);

    const headerPanel = (
      <>
        <EditorHeaderRowLeft>
          {!isTeacher && <HeaderUserImage src={getImageUrl(fileService, image, '60x60')} title={full_name} />}
          <HeaderUserName>{isTeacher ? i18n.__('app_student_editor') : full_name}</HeaderUserName>
          <HeaderDate>{new Date(updated_at).toLocaleDateString(window.baseLocale)}</HeaderDate>
        </EditorHeaderRowLeft>
        <EditorHeaderRowRight>
          {draft && isEditable ? (
            <HeaderToolbar
              isEditable={isEditable}
              report={report}
              editorId={editorId}
              isTeacher={isTeacher}
              showToolbar={showToolbar[post_uuid || 'current'] || false}
              setShowToolbar={() => toggleToolbar(post_uuid)}
            />
          ) : (
            <>
              <ImmersiveReaderButton />
              {isEditable && (
                <BlogPostOptions className={'ir-hide'}>
                  <BlogPostOptionEdit disabled={!post_uuid} onClick={() => editPost(blog, index)}>
                    {i18n.__('app_edit')}
                  </BlogPostOptionEdit>
                  <BlogPostOptionEdit disabled={!post_uuid} onClick={() => removePost(post_uuid)}>
                    {i18n.__('app_remove')}
                  </BlogPostOptionEdit>
                </BlogPostOptions>
              )}
              <HeaderRequestFeedback
                disabled={!post_uuid}
                onClick={() => toggleReportComments(post_uuid, type)}
                src={CommentsIcon}
              />
            </>
          )}
        </EditorHeaderRowRight>
      </>
    );

    const editorPanel = index === -1 ? currentEditorPanel() : editorPanels[index];

    const buttonPanel = draft && isEditable && (
      <>
        <AutoSaveStatus>{autoSaveStatus[editorId]}</AutoSaveStatus>
        {isEditable && (
          <ButtonPanel>
            <PrimaryButton
              text={i18n.__('app_publish_post')}
              onClick={() => publishReport(blog, index)}
              disabled={!post_uuid || isTeacher || !content}
              className="publish"
            />
          </ButtonPanel>
        )}
      </>
    );

    return (
      <BlogWrapper key={post_uuid || index}>
        <NodeActivityEditor
          type={type}
          disabled={!draft || !isEditable}
          headerPanel={headerPanel}
          editorPanel={editorPanel}
          buttonPanel={buttonPanel}
        />
      </BlogWrapper>
    );
  };

  return (
    <>
      <ViewSection>
        {(isEditable || isTeacher) && addPost(currentPost, -1)}
        {(posts || []).map((post, index) => addPost(post, index))}
        {!isEditable && posts?.length === 0 && <i>{i18n.__('logbook_no_posts')}</i>}
      </ViewSection>
      {!isTeacher && (
        <SubmitPanel className={'ir-hide'}>
          {isLocked ? (
            <LockInfo>{i18n.__('editor_locked_desc')}</LockInfo>
          ) : isEditable ? (
            <SecondaryButton text={i18n.__('app_handin')} onClick={submitReport} disabled={isTeacher} />
          ) : (
            <PrimaryButton text={i18n.__('app_edit')} onClick={revokeReport} disabled={isTeacher} />
          )}
        </SubmitPanel>
      )}
    </>
  );
};

export default PrivateBlogContentViewer;

PrivateBlogContentViewer.propTypes = {
  type: PropTypes.string.isRequired,
  report: PropTypes.object,
  isEditable: PropTypes.bool.isRequired,
  saveReport: PropTypes.func.isRequired,
  fileService: PropTypes.object.isRequired,
  integrations: PropTypes.object.isRequired,
  autoSaveStatus: PropTypes.object.isRequired,
  isTeacher: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
  sessionId: PropTypes.string.isRequired,
  nodeId: PropTypes.string.isRequired,
};
